/**
 * Button Component
 *
 * This file defines a customizable Button component using React and Radix UI.
 * It utilizes class-variance-authority for managing class variations and
 * provides a wide range of customization options through props.
 *
 * Key Features:
 * - Customizable variants: default, destructive, outline, secondary, ghost, link
 * - Adjustable sizes: default, sm, lg, icon
 * - Loading state with spinner
 * - Asynchronous onClick handling
 * - Accessibility support
 *
 * Props:
 * @param {string} className - Additional CSS classes
 * @param {string} variant - Button style variant
 * @param {string} size - Button size
 * @param {boolean} asChild - Render as child component
 * @param {boolean} loading - Show loading spinner
 * @param {function} onClick - Click event handler (supports async)
 * @param {React.ReactNode} children - Button content
 * @param {React.Ref} ref - Forwarded ref
 *
 * Usage:
 * ```jsx
 * import { Button } from "@/components/ui/button"
 *
 * <Button variant="outline" size="lg" onClick={handleClick}>
 *   Click me
 * </Button>
 * ```
 *
 * The component uses the `cn` utility function from "@/lib/utils" for
 * conditional class name generation.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const Spinner = ({ className, mode = "light" }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={cn(className, {
      "text-system-crystalWhite": mode === "light",
      "text-midnightIndigo-400": mode === "dark",
    })}
  >
    <style>
      {`.spinner_OSmW{transform-origin:center;animation:spinner_T6mA .75s step-end infinite}@keyframes spinner_T6mA{8.3%{transform:rotate(30deg)}16.6%{transform:rotate(60deg)}25%{transform:rotate(90deg)}33.3%{transform:rotate(120deg)}41.6%{transform:rotate(150deg)}50%{transform:rotate(180deg)}58.3%{transform:rotate(210deg)}66.6%{transform:rotate(240deg)}75%{transform:rotate(270deg)}83.3%{transform:rotate(300deg)}91.6%{transform:rotate(330deg)}100%{transform:rotate(360deg)}}`}
    </style>
    <g className="spinner_OSmW">
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        opacity=".14"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(30 12 12)"
        opacity=".29"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(60 12 12)"
        opacity=".43"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(90 12 12)"
        opacity=".57"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(120 12 12)"
        opacity=".71"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(150 12 12)"
        opacity=".86"
        fill="currentColor"
      />
      <rect
        x="11"
        y="1"
        width="2"
        height="5"
        transform="rotate(180 12 12)"
        fill="currentColor"
      />
    </g>
  </svg>
);

const buttonVariants = cva(
  "inline-flex  items-center justify-center  !leading-[0] whitespace-nowrap rounded  wireless-Medium ring-offset-background transition-colors transition-all duration-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 pt-[2px]",
  {
    variants: {
      variant: {
        primary:
          "bg-system-primary text-system-crystalWhite hover:bg-system-active ",

        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",

        secondary:
          "bg-white text-midnightIndigo-400 text-secondary-foreground border border-midnightIndigo-200 hover:border-midnightIndigo-300 hover:!bg-system-white transition-all duration-300 ",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-midnightIndigo-400 hover:!bg-system-white transition-all duration-300",

        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary cursor-pointer underline-offset-4 hover:underline",
      },
      size: {
        default: " h-[36px] w-[102px]  sm:w-[107px] md:h-[42px] md:w-[112px]",
        sm: "\
             h-[26px] w-[88px]   text-[14px]     \
             md:w-[94px] md:h-[30px] md:text-[14px] \
             lg:h-[30px] lg:w-[99px] lg:text-[15px]",

        md: "\
             h-[36px] w-[102px]   text-[14px]     \
             md:w-[107px] md:h-[36px] md:text-[15px] \
             lg:h-[42px] lg:w-[112px] lg:text-[16px]",
        lg: "h-11 rounded-md px-8",
        xl: "text-[14px] md:text-[15px] lg:text-[16px] w-full h-[42px]",
        icon: "h-10 w-10 p-2 hover:bg-system-white",
        smicon: "h-[30px] w-[30px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  (
    { className, variant, size, asChild = false, loading = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return loading ? (
      <Comp
        autoFocus={false}
        className={cn(
          buttonVariants({ variant, size, className }),
          "transition-all duration-300",
          loading && "opacity-100 cursor-not-allowed",
          "pointer-events-none"
        )}
        ref={ref}
      >
        <Spinner
          mode={variant === "outline" || variant === "ghost" ? "dark" : "light"}
        />
      </Comp>
    ) : (
      <Comp
        autoFocus={false}
        className={cn(
          buttonVariants({ variant, size, className }),
          "transition-all duration-300",
          loading && "opacity-100 cursor-not-allowed"
        )}
        disabled={loading}
        ref={ref}
        {...props}
      >
        {props.children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
